import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import { useNavigate } from 'react-router-dom';

import { PanelTitle, InfoCard, ArtifactsNews } from 'ui';
import { NewsFeedSettings } from 'modules/common';

import { useArtifacts } from 'shared/hooks';
import { creditReviewInitialSubscription } from 'shared/constants/creditReview/creditReviewInitialSubscription';
import { getNewsFeedAction, saveFeedSettingsAction } from 'store/newsFeed/newsFeedActions';
import creditReviewFreeTrialInvitationImg from 'assets/images/credit-review-free-trial-invitation.jpg';
import shakehandImg from 'assets/images/shakehand.png';
import { getHomeSubscriptions, getShopProducts } from 'modules/shop/selectors/shopSelectors';
import { saveOnboardingState } from 'store/localStorage';
import { subscriptionStatuses } from 'modules/shop/shared/constants/shopSubscriptionStatuses';
import { useFeatures } from 'shared/hooks/useFeatures/useFeatures';

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
  },
}));

const NewsFeed = () => {
  const newsFeedRef = useRef(null);
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const features = useFeatures();

  const [toggle, setToggle] = useState(false);

  const handleClick = () => {
    setToggle(!toggle);
  };

  const { data, isPending, total, settings } = useSelector(({ newsFeed }: any) => newsFeed);
  const { data: shopProductsData, isPending: shopProductsIsPending } = useSelector(getShopProducts);

  const { data: subscriptionsData, isPending: subscriptionsIsPending } =
    useSelector(getHomeSubscriptions);

  const showSubscriptionsInvitationCard = subscriptionsData.length === 0;

  const creditReviewSubscription = creditReviewInitialSubscription(shopProductsData);

  const showCreditReviewInvitationCard =
    features.creditReviewInvitationCard &&
    creditReviewSubscription &&
    creditReviewSubscription.status === subscriptionStatuses.NO_SUBSCRIPTION;

  const { limit, handleChangeArtifactsPage, handleArtifactClick, currentPage } = useArtifacts({
    getArtifactsAction: getNewsFeedAction,
    getArtifactsDataActionParams: {
      settings: settings.newsFeed,
    },
    customLimit: 10,
    ref: newsFeedRef,
    useUrlParam: true,
  } as any);

  const newsSettingsChanged = (updatedSettings: any) => {
    dispatch(saveFeedSettingsAction({ settings: updatedSettings }));
  };

  const navigateToSubscriptions = () => navigate('/subscriptions');

  const navigateToCreditReviewSubscription = () => {
    saveOnboardingState(true);
    navigate(
      `/subscriptions?productId=${
        creditReviewSubscription ? creditReviewSubscription.id : undefined
      }`,
    );
  };

  return (
    <div ref={newsFeedRef}>
      <PanelTitle
        button={
          <Button variant="outlined" size="small" onClick={handleClick}>
            {t('News settings')}
          </Button>
        }
      >
        {t('News feed')}
      </PanelTitle>
      <Collapse in={toggle} timeout="auto">
        <NewsFeedSettings newsSettingsChanged={newsSettingsChanged} />
      </Collapse>
      {!shopProductsIsPending && showCreditReviewInvitationCard && (
        <Box mb={3}>
          <InfoCard
            onClick={navigateToCreditReviewSubscription}
            title={t('CREDIT_REVIEW.FREE_TRIAL_INVITATION_CARD_TITLE')}
            text={t('CREDIT_REVIEW.FREE_TRIAL_INVITATION_CARD_DESC')}
            imgUrl={creditReviewFreeTrialInvitationImg}
            contentHeight={240}
          />
        </Box>
      )}
      {!subscriptionsIsPending && showSubscriptionsInvitationCard && (
        <Box mb={3}>
          <InfoCard
            onClick={navigateToSubscriptions}
            title={t('Looking to subscribe to our Scope products?')}
            text={t(
              'Introducing Scope Subscriptions:\nBrowse the subscription center and start selected subscriptions free of charge or get your free trials.',
            )}
            imgUrl={shakehandImg}
          />
        </Box>
      )}
      <div className={classes.container}>
        <ArtifactsNews
          data={data}
          isPending={isPending}
          total={total}
          limit={limit}
          currentPage={currentPage}
          onItemClick={handleArtifactClick}
          onChangePage={handleChangeArtifactsPage}
        />
      </div>
    </div>
  );
};

export default NewsFeed;
